import type { drivers, drivers_pk_columns_input, drivers_set_input } from '$lib/tools/graphdb';
import { create_store } from '$lib/tools/graph-db/stores/create_store.svelte';
import { graphql } from '$lib/tools/graphdb';

export type Driver = drivers;

const DRIVER_FIELDS = {
	is_active: true,
	id: true,
	image_id: true,
	phone_number: true,
	name: true,
	force_sync: true,
	duty_time_windows_utc: true,
	date_updated: true,
	date_created: true,
	computed_balance: true,
	computed_monthly_earnings: true
} as const;

export const driver_store = create_store<drivers>({
	name: 'driver',
	keys: { last_updated: 'date_updated', main: 'date_created' },
	fetch: {
		// Full-fetch contract (amendment A2): the driver is the only 1-row
		// store, so `many` deliberately ignores any incremental `where`.
		many: () => graphql.query_many('drivers', undefined, DRIVER_FIELDS) as Promise<drivers[] | null>,
		one: (id) => graphql.query_one('drivers_by_pk', { id }, DRIVER_FIELDS) as Promise<drivers | null>
	},
	stream: (max) => (on_batch) =>
		graphql.subscribe(
			'drivers_stream',
			{ batch_size: 10, cursor: [{ initial_value: { date_updated: max } }] },
			DRIVER_FIELDS,
			(rows) => on_batch(rows),
			(error) => console.error('drivers_stream error', error)
		),
	mutate: {
		update: (input: { pk_columns: drivers_pk_columns_input; _set: drivers_set_input }) =>
			graphql.mutate('update_drivers_by_pk', input, DRIVER_FIELDS) as Promise<drivers | null>
	},
	auto: { populate: true, subscribe: true, prune: false }
});

// Single-row profile: a scoped view over `date_created` asc gives us the
// first/only driver row, reactively. RAM = 1 row.
const driver_view = driver_store.where({ index: 'date_created', sort: 'asc' });

export const driver_state = {
	get value(): drivers | null {
		return driver_view.values[0] ?? null;
	},

	populate: () => driver_store.sync.populate(),

	/** Force a full refetch of the profile row from the server. */
	refresh: async (): Promise<drivers | null> => {
		const rows = await driver_store.sync.refresh();
		return rows?.[0] ?? null;
	},

	/** Update the driver row; returns the refreshed row or null on failure. */
	update_one: (args: { input: { pk_columns: drivers_pk_columns_input; _set: drivers_set_input } }) =>
		driver_store.mutate.update(args.input),

	get_driver_id: async (): Promise<string> => {
		const stored_driver = driver_view.values[0];
		if (stored_driver?.id) return stored_driver.id;

		const drivers = await driver_store.sync.populate();
		const driver = drivers?.[0];
		if (!driver?.id) throw new Error('no_driver_id');
		return driver.id;
	},

	stored_value: async (): Promise<drivers | null> => {
		const values = await driver_store.materialize();
		return values?.[0] ?? null;
	},

	start: () => driver_store.sync.start(),
	reset: () => driver_store.reset()
};
